import { scry } from "@/lib/scryClient";
import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import AddToCollectionModal from "@/components/AddToCollectionModal"; // adjust path if different

type ScryfallCardLite = {
  id: string;
  name: string;
  set?: string | null;
  collector_number?: string | null;
  image_uris?: { normal?: string | null; large?: string | null } | null;
  card_faces?: { image_uris?: { normal?: string | null; large?: string | null } | null }[] | null;
  prices?: { usd?: string | null; usd_foil?: string | null } | null;
};

type Scryfallish = {
  id?: string | null;
  oracle_id?: string | null;
  name?: string | null;
  set?: string | null;
  set_name?: string | null;
  lang?: string | null;
  collector_number?: string | null;
  image_uris?: {
    normal?: string | null;
    large?: string | null;
  } | null;
  card_faces?: {
    image_uris?: {
      normal?: string | null;
      large?: string | null;
    } | null;
  }[] | null;
};

export type ScanOCRModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Make optional: if provided, we call it but do not rely on it
  onPick?: (card: ScryfallCardLite, seeds?: { usd?: number | null; usd_foil?: number | null }) => void;
};

// Normalize scry responses (Response or JSON)
async function normalizeScry<T = any>(resOrJson: any): Promise<T> {
  return typeof resOrJson?.json === "function" ? await resOrJson.json() : (resOrJson as T);
}

// Helper to map scanner result -> Scryfallish used by collection modals
function toScryfallish(c: ScryfallCardLite): Scryfallish {
  return {
    id: c.id ?? null,
    name: c.name ?? null,
    set: c.set ?? null,
    collector_number: c.collector_number ?? null,
    image_uris: c.image_uris
      ? { normal: c.image_uris.normal ?? null, large: c.image_uris.large ?? null }
      : null,
    card_faces: Array.isArray(c.card_faces)
      ? c.card_faces.map((f) => ({
          image_uris: f?.image_uris
            ? { normal: f.image_uris.normal ?? null, large: f.image_uris.large ?? null }
            : null,
        }))
      : null,
  };
}

// Use backend scry client (normalized)
async function scryfallNamedFuzzy(name: string): Promise<ScryfallCardLite | null> {
  const named = await normalizeScry(await scry.search(`!"${name}"`, { unique: "prints", order: "released", dir: "desc" }));
  const base = Array.isArray((named as any)?.data) ? (named as any).data[0] : null;
  if (!base) return null;
  return {
    id: base.id,
    name: base.name,
    set: base.set,
    collector_number: base.collector_number,
    image_uris: base.image_uris,
    card_faces: base.card_faces,
    prices: base.prices,
  } as any;
}

// Route exact-name prints search through backend (no public API)
async function scryfallSearchPrintsByNameExact(name: string): Promise<ScryfallCardLite[]> {
  const j = await normalizeScry(await scry.search(`!"${name}"`, { unique: "prints", order: "released", dir: "desc" }));
  return Array.isArray((j as any)?.data) ? (((j as any).data as any[]) as ScryfallCardLite[]) : [];
}

// Prefer set + collector number when available; normalize backend response
async function scryfallSearchBySetAndNumber(name: string, set: string, cn: string): Promise<ScryfallCardLite | null> {
  const q = `!"${name}" set:${set} cn:${cn}`;
  const data = await normalizeScry(await scry.search(q, { unique: "prints", order: "released", dir: "desc" }));
  if (!Array.isArray((data as any)?.data) || !(data as any).data.length) return null;
  return ((data as any).data[0] as any) as ScryfallCardLite;
}

export default function ScanOCRModal({ open, onOpenChange, onPick }: ScanOCRModalProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scanActiveRef = useRef(false);
  const ocrBusyRef = useRef(false);
  const [scanStatus, setScanStatus] = useState("");
  const [cands, setCands] = useState<ScryfallCardLite[]>([]);

  // NEW: local AddToCollection modal state
  const [addOpen, setAddOpen] = useState(false);
  const [addCard, setAddCard] = useState<Scryfallish | null>(null);
  const [addSeeds, setAddSeeds] = useState<{ usd?: number | null; usd_foil?: number | null } | null>(null);

  async function ensureCamera() {
    if (streamRef.current) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) {
        (videoRef.current as any).srcObject = stream;
        try {
          await videoRef.current.play();
        } catch {
          // iOS/Safari may reject without user gesture
        }
      }
    } catch (e: any) {
      setScanStatus(e?.message ?? "Camera error");
    }
  }
  function stopCamera() {
    scanActiveRef.current = false;
    try { streamRef.current?.getTracks()?.forEach((t) => t.stop()); } catch {}
    streamRef.current = null;
    if (videoRef.current) {
      try { videoRef.current.pause(); } catch {}
      (videoRef.current as any).srcObject = null;
    }
  }

  async function tesseractDetectLines(src: HTMLCanvasElement): Promise<string[]> {
    try {
      setScanStatus("OCR…");
      // Dynamic import so build doesn’t require tesseract unless used
      const Tesseract = (await import("tesseract.js")).default;
      const { data } = await Tesseract.recognize(src, "eng");
      const raw = Array.isArray((data as any)?.lines) && (data as any).lines.length
        ? (data as any).lines.map((l: any) => String(l?.text || ""))
        : String((data as any)?.text || "").split(/\r?\n/);
      return Array.from(new Set(raw.map((s) => s.trim()).filter(Boolean)));
    } catch {
      return [];
    }
  }

  async function runOcrOnce(): Promise<boolean> {
    const v = videoRef.current;
    if (!v) return false;
    const w = v.videoWidth || 640;
    const h = v.videoHeight || 480;
    if (w === 0 || h === 0) return false;
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return false;
    ctx.drawImage(v, 0, 0, w, h);

    setScanStatus("Reading…");
    const lines = await tesseractDetectLines(canvas);
    if (!Array.isArray(lines) || lines.length === 0) return false;
    setScanStatus("Matching…");

    const all = lines.join(" ").replace(/[^\w\s\-’'()#]/g, " ");

    // Precise type-line filter (don’t block hyphenated names)
    const typeLineLike = /^(?:Legendary\s+)?(?:Creature|Instant|Sorcery|Artifact|Enchantment|Planeswalker|Battle|Basic\s+Land|Land|Token)\b/i;

    const nameCands = Array.from(
      new Set(
        lines
          .map((s) => s.trim())
          .filter((s) => s.length >= 3)
          .filter((s) => !typeLineLike.test(s)) // only skip if the entire line looks like a type line
          .map((s) => s.replace(/\s{2,}/g, " "))
      )
    )
      .sort((a, b) => b.length - a.length)
      .slice(0, 5);

    const results: ScryfallCardLite[] = [];
    const seen = new Set<string>();

    if (mSetNum && nameCands.length > 0) {
      const setCode = mSetNum[1].toUpperCase();
      const cn = mSetNum[2];
      const name = nameCands[0];
      try {
        const card = await scryfallSearchBySetAndNumber(name, setCode, cn);
        if (card?.id && !seen.has(card.id)) { seen.add(card.id); results.push(card as any); }
      } catch {}
    }

    for (const n of nameCands) {
      try {
        const prints = await scryfallSearchPrintsByNameExact(n);
        for (const p of prints || []) {
          if (p?.id && !seen.has(p.id)) { seen.add(p.id); results.push(p as any); }
        }
      } catch {}
    }

    for (const n of nameCands) {
      try {
        const fuzzy = await scryfallNamedFuzzy(n);
        if (fuzzy?.id && !seen.has(fuzzy.id)) { seen.add(fuzzy.id); results.push(fuzzy as any); }
      } catch {}
    }

    setCands(results.slice(0, 10));
    return results.length > 0;
  }

  async function startOcrLoop() {
    scanActiveRef.current = true;
    setScanStatus("Scanning…");
    await new Promise((r) => setTimeout(r, 200));
    while (scanActiveRef.current && videoRef.current && streamRef.current) {
      if (ocrBusyRef.current) { await new Promise((r) => setTimeout(r, 100)); continue; }
      ocrBusyRef.current = true;
      try { await runOcrOnce(); } catch {} finally { ocrBusyRef.current = false; }
      await new Promise((r) => setTimeout(r, 700));
    }
    setScanStatus("");
  }

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!open) { // early exit on close
        stopCamera();
        return;
      }
      await ensureCamera();
      if (!mounted) return;
      setCands([]);
      await startOcrLoop();
    })();
    return () => { mounted = false; };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) stopCamera(); onOpenChange(o); }}>
      <DialogContent className="md:max-w-[min(700px,95vw)] p-0 overflow-hidden border border-gray-800 bg-gray-950 text-white">
        <DialogHeader className="px-4 py-3 border-b border-gray-800 bg-black/30">
          <DialogTitle className="flex items-center justify-between">
            <span>Scan Card (OCR)</span>
            <span className="text-xs text-slate-300">{scanStatus}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3 p-4">
          <div className="rounded-lg overflow-hidden border border-slate-700 bg-black">
            <video ref={videoRef} playsInline muted className="w-full h-[48vh] object-cover bg-black" />
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" className="border-gray-800 text-slate-200 hover:bg-white/5" onClick={() => void runOcrOnce()}>
              Try now
            </Button>
            <Button variant="outline" className="border-gray-800 text-slate-200 hover:bg-white/5" onClick={() => { if (!scanActiveRef.current) void startOcrLoop(); }}>
              Resume auto
            </Button>
            <div className="ml-auto">
              <Button variant="outline" className="border-gray-800 text-slate-200 hover:bg-white/5" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </div>
          </div>

          <div className="mt-1">
            <div className="text-xs opacity-70">Matches</div>
            {cands.length === 0 ? (
              <div className="text-sm text-white/70">No matches yet.</div>
            ) : (
              <ul className="max-h-[35vh] overflow-auto divide-y divide-gray-800">
                {cands.map((c) => {
                  const img =
                    c.image_uris?.normal ||
                    c.image_uris?.large ||
                    c.card_faces?.[0]?.image_uris?.normal ||
                    c.card_faces?.[0]?.image_uris?.large ||
                    null;
                  return (
                    <li key={c.id} className="py-2 flex items-center gap-3">
                      <img src={img || "/images/cardback.png"} alt={c.name} className="w-12 h-16 rounded border border-gray-700 object-cover" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-white truncate">{c.name}</div>
                        <div className="text-xs text-white/70 truncate">
                          {(c.set || "").toUpperCase()} {c.collector_number ? `• #${c.collector_number}` : ""}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        className="border-amber-600 text-amber-300 hover:bg-amber-500/10"
                        title="Add to my collection"
                        onClick={() => {
                          // Build price seeds
                          const usd = c.prices?.usd ? Number(c.prices.usd) : null;
                          const usd_foil = c.prices?.usd_foil ? Number(c.prices.usd_foil) : null;
                          const seeds = {
                            usd: Number.isFinite(usd as any) ? (usd as number) : null,
                            usd_foil: Number.isFinite(usd_foil as any) ? (usd_foil as number) : null,
                          };

                          // Stop scanning and open AddToCollection flow
                          stopCamera();
                          setAddCard(toScryfallish(c));
                          setAddSeeds(seeds);
                          setAddOpen(true);

                          // Optional: still call onPick for analytics/back-compat but ignore errors
                          try { onPick?.(c, seeds); } catch {}
                          // Close scanner while adding
                          onOpenChange(false);
                        }}
                      >
                        ♥ Add
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="text-xs opacity-70">Tip: Fill the frame with the card face; avoid glare; keep text sharp.</div>
        </div>
      </DialogContent>

      {/* NEW: Add to Collection modal triggered by scanner */}
      {addCard && (
        <AddToCollectionModal
          open={addOpen}
          onClose={() => setAddOpen(false)}
          card={addCard}
          // If your AddToCollectionModal supports price seeding, pass it here (adjust prop name if needed):
          // seedPrices={addSeeds}
          onAdded={() => {
            setAddOpen(false);
            // Clear selection but keep last add card in case user reopens
            // setAddCard(null);
          }}
        />
      )}
    </Dialog>
  );
}

